import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { ScrollArea } from './ui/scroll-area';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  DollarSign, Upload, FileText, Building2, Plug, RefreshCw, Check,
  Loader2, Sparkles, ArrowRight, AlertTriangle, Plus, Trash2, Receipt, X,
  Brain, TrendingUp, BarChart3, Scale, Target, ChevronLeft, ChevronRight,
} from 'lucide-react';
import SidebarLayout from './SidebarLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const FINANCIAL_SECTIONS = [
  { label: 'Overview', items: [
    { id: 'spend', label: 'Spend Overview', icon: DollarSign },
    { id: 'analysis', label: 'AI Analysis', icon: Brain },
    { id: 'alerts', label: 'Anomaly Alerts', icon: AlertTriangle },
  ]},
  { label: 'Planning', items: [
    { id: 'budget', label: 'Spend vs Budget', icon: Scale },
    { id: 'health', label: 'Health Scorecards', icon: BarChart3 },
    { id: 'benchmarks', label: 'Cost Benchmarks', icon: Target },
  ]},
  { label: 'Tracking', items: [
    { id: 'invoices', label: 'Invoices', icon: Receipt },
    { id: 'reconciliation', label: 'Reconciliation', icon: Scale },
  ]},
  { label: 'Setup', items: [
    { id: 'connectors', label: 'ERP Connectors', icon: Plug },
  ]},
];

// Mini sparkline SVG component
function Sparkline({ data, width = 80, height = 24, color = '#6366F1' }) {
  if (!data || data.length < 2) return null;
  const values = data.map(d => d.amount || 0);
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = max - min || 1;
  const points = values.map((v, i) => `${(i / (values.length - 1)) * width},${height - ((v - min) / range) * (height - 4) - 2}`).join(' ');
  return (
    <svg width={width} height={height} className="shrink-0">
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function FinancialHub({ token, role }) {
  const [tab, setTab] = useState('spend');
  const [connectors, setConnectors] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [spendSummary, setSpendSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showInvoiceUpload, setShowInvoiceUpload] = useState(false);
  const [invoiceText, setInvoiceText] = useState('');
  const [parsedInvoice, setParsedInvoice] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [savingInvoice, setSavingInvoice] = useState(false);
  const [vendors, setVendors] = useState([]);

  // AI Spend Analysis
  const [spendAnalysis, setSpendAnalysis] = useState(null);
  const [analyzingSpend, setAnalyzingSpend] = useState(false);

  // Spend vs Budget
  const [budgetData, setBudgetData] = useState(null);

  // Contract reconciliation
  const [reconData, setReconData] = useState(null);

  // File upload
  const [uploadingFile, setUploadingFile] = useState(false);
  const fileInputRef = React.useRef(null);

  // Spend trends (sparklines)
  const [spendTrends, setSpendTrends] = useState(null);

  // Anomaly alerts
  const [anomalies, setAnomalies] = useState(null);

  // Financial health scorecards
  const [healthScores, setHealthScores] = useState(null);

  // Cost benchmarks
  const [benchmarks, setBenchmarks] = useState(null);
  const [benchView, setBenchView] = useState('property'); // property, platform, service

  // Financial identifiers dialog
  const [showIdentifiers, setShowIdentifiers] = useState(false);
  const [identEntity, setIdentEntity] = useState({ type: '', id: '', name: '' });
  const [identifiers, setIdentifiers] = useState({});
  const [savingIdent, setSavingIdent] = useState(false);

  const isAdmin = role === 'admin';
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [connRes, invRes, spendRes, vendRes, budgetRes, reconRes, trendRes, anomRes, healthRes, benchRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/financial/connectors`, { headers }),
        axios.get(`${BACKEND_URL}/api/invoices?limit=50`, { headers }),
        axios.get(`${BACKEND_URL}/api/reports/vendor-spend-summary`, { headers }),
        axios.get(`${BACKEND_URL}/api/vendors`, { headers }),
        axios.get(`${BACKEND_URL}/api/reports/spend-vs-budget`, { headers }).catch(() => ({ data: null })),
        axios.get(`${BACKEND_URL}/api/reports/contract-invoice-reconciliation`, { headers }).catch(() => ({ data: null })),
        axios.get(`${BACKEND_URL}/api/reports/vendor-spend-trends`, { headers }).catch(() => ({ data: null })),
        axios.get(`${BACKEND_URL}/api/reports/spend-anomalies`, { headers }).catch(() => ({ data: null })),
        axios.get(`${BACKEND_URL}/api/reports/financial-health`, { headers }).catch(() => ({ data: null })),
        axios.get(`${BACKEND_URL}/api/reports/cost-benchmarks`, { headers }).catch(() => ({ data: null })),
      ]);
      setConnectors(connRes.data.connectors || []);
      setInvoices(invRes.data.invoices || []);
      setSpendSummary(spendRes.data);
      setVendors(vendRes.data || []);
      setBudgetData(budgetRes.data);
      setReconData(reconRes.data);
      setSpendTrends(trendRes.data);
      setAnomalies(anomRes.data);
      setHealthScores(healthRes.data);
      setBenchmarks(benchRes.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Parse invoice via AI
  const parseInvoice = async () => {
    if (!invoiceText.trim()) return;
    setParsing(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/invoices/parse`, { text: invoiceText }, { headers });
      setParsedInvoice(res.data);
      toast.success('Invoice parsed by AI');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Parse failed');
    } finally { setParsing(false); }
  };

  // Save parsed invoice
  const saveInvoice = async () => {
    if (!parsedInvoice) return;
    setSavingInvoice(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/invoices`, {
        vendor_id: parsedInvoice.matched_vendor?.id || '',
        vendor_name: parsedInvoice.vendor_name || '',
        invoice_number: parsedInvoice.invoice_number || '',
        invoice_date: parsedInvoice.invoice_date || '',
        due_date: parsedInvoice.due_date || '',
        total_amount: parsedInvoice.total_amount || 0,
        currency: parsedInvoice.currency || 'USD',
        line_items: parsedInvoice.line_items || [],
        notes: parsedInvoice.notes || '',
        ai_extracted: true,
        status: 'pending',
      }, { headers });
      toast.success('Invoice saved');
      setShowInvoiceUpload(false);
      setParsedInvoice(null);
      setInvoiceText('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSavingInvoice(false); }
  };

  // Financial identifiers
  const openIdentifiers = async (entityType, entityId, entityName) => {
    setIdentEntity({ type: entityType, id: entityId, name: entityName });
    try {
      const res = await axios.get(`${BACKEND_URL}/api/financial/identifiers/${entityType}/${entityId}`, { headers });
      setIdentifiers(res.data || {});
    } catch { setIdentifiers({}); }
    setShowIdentifiers(true);
  };

  const saveIdentifiers = async () => {
    setSavingIdent(true);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/financial/identifiers/${identEntity.type}/${identEntity.id}`, identifiers, { headers });
      toast.success('Financial identifiers saved');
      setShowIdentifiers(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSavingIdent(false); }
  };

  // Toggle connector
  const toggleConnector = async (connectorType, enabled) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/financial/connectors/${connectorType}`, {
        connector_type: connectorType, enabled, sync_frequency: 'daily',
      }, { headers });
      toast.success(enabled ? 'Connector enabled' : 'Connector disabled');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const syncConnector = async (connectorType) => {
    try {
      await axios.post(`${BACKEND_URL}/api/admin/financial/connectors/${connectorType}/sync`, {}, { headers });
      toast.success('Sync triggered');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sync failed');
    }
  };

  const catColors = { accounting: '#2563EB', erp: '#7C3AED', expense: '#D97706', procurement: '#059669', ap: '#0891B2', payment: '#DC2626', manual: '#6B7280' };

  const runSpendAnalysis = async () => {
    setAnalyzingSpend(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/ai/spend-analysis`, {}, { headers });
      setSpendAnalysis(res.data);
      toast.success('AI spend analysis complete');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Analysis failed');
    } finally { setAnalyzingSpend(false); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${BACKEND_URL}/api/admin/invoices/upload`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      if (res.data.parsed) {
        setParsedInvoice(res.data.parsed);
        setShowInvoiceUpload(true);
        toast.success(`Parsed ${file.name}`);
      } else {
        setInvoiceText(res.data.text || '');
        setShowInvoiceUpload(true);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const insightColors = { anomaly: 'bg-red-50 text-red-700 border-l-red-400', opportunity: 'bg-emerald-50 text-emerald-700 border-l-emerald-400', risk: 'bg-amber-50 text-amber-700 border-l-amber-400', trend: 'bg-blue-50 text-blue-700 border-l-blue-400' };

  const alertColors = { duplicate: 'bg-red-50 border-l-red-500', budget_overrun: 'bg-amber-50 border-l-amber-500', spike: 'bg-orange-50 border-l-orange-500', untracked: 'bg-blue-50 border-l-blue-400' };
  const alertIcons = { duplicate: AlertTriangle, budget_overrun: TrendingUp, spike: TrendingUp, untracked: FileText };

  const alertBadge = (anomalies?.summary?.high || 0) > 0 ? anomalies.summary.high : undefined;
  const financialGroupsWithBadges = FINANCIAL_SECTIONS.map(g => ({
    ...g,
    items: g.items.map(i => i.id === 'alerts' && alertBadge ? { ...i, badge: alertBadge } : i),
  }));

  const sidebarFooter = isAdmin ? (
    <div className="space-y-1">
      <input ref={fileInputRef} type="file" accept=".pdf,.txt,.csv" className="hidden" onChange={handleFileUpload} />
      <Button size="sm" variant="ghost" className="w-full text-xs gap-1.5 justify-start h-7" onClick={() => fileInputRef.current?.click()} disabled={uploadingFile} data-testid="upload-file-btn">
        <Upload className="h-3 w-3" /> {uploadingFile ? 'Parsing...' : 'Upload File'}
      </Button>
      <Button size="sm" variant="ghost" className="w-full text-xs gap-1.5 justify-start h-7" onClick={() => setShowInvoiceUpload(true)} data-testid="upload-invoice-btn">
        <FileText className="h-3 w-3" /> Paste Invoice
      </Button>
    </div>
  ) : null;

  return (
    <SidebarLayout title="Financial" icon={DollarSign} groups={financialGroupsWithBadges} activeId={tab} onSelect={setTab} footer={sidebarFooter}>
        <ScrollArea className="h-full">
          <div className="p-5">
          {/* Spend Overview */}
          {tab === 'spend' && (
            <div className="space-y-6">
              {/* Summary cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="border rounded-xl p-4 bg-card">
                  <div className="text-xs text-muted-foreground mb-1">Total Vendor Spend</div>
                  <div className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>
                    ${(spendSummary?.total_spend || 0).toLocaleString()}
                  </div>
                </div>
                <div className="border rounded-xl p-4 bg-card">
                  <div className="text-xs text-muted-foreground mb-1">Vendors with Spend Data</div>
                  <div className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>{spendSummary?.vendor_count || 0}</div>
                </div>
                <div className="border rounded-xl p-4 bg-card">
                  <div className="text-xs text-muted-foreground mb-1">Invoices Tracked</div>
                  <div className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>{invoices.length}</div>
                </div>
              </div>

              {(spendSummary?.vendors || []).length > 0 ? (
                <div className="border rounded-xl overflow-hidden">
                  <div className="p-3 bg-muted/30 border-b">
                    <h3 className="text-sm font-semibold">Vendor Spend Ranking</h3>
                  </div>
                  <div className="divide-y">
                    {spendSummary.vendors.slice(0, 20).map((v, i) => {
                      const trend = (spendTrends?.vendors || []).find(t => t.vendor_id === v.vendor_id);
                      return (
                        <div key={v.vendor_id} className="flex items-center gap-3 p-3 text-sm hover:bg-muted/20">
                          <span className="text-xs text-muted-foreground w-6 text-right">#{i + 1}</span>
                          <span className="font-medium flex-1">{v.vendor_name}</span>
                          {trend && <Sparkline data={trend.data} color={trend.trend === 'up' ? '#DC2626' : trend.trend === 'down' ? '#16A34A' : '#6366F1'} />}
                          {trend?.trend && (
                            <Badge variant="outline" className={`text-[8px] ${trend.trend === 'up' ? 'text-red-600 border-red-300' : trend.trend === 'down' ? 'text-emerald-600 border-emerald-300' : 'text-muted-foreground'}`}>
                              {trend.trend === 'up' ? 'Rising' : trend.trend === 'down' ? 'Falling' : 'Stable'}
                            </Badge>
                          )}
                          <span className="text-xs text-muted-foreground">{v.record_count} records</span>
                          <span className="font-bold tabular-nums">${v.total_spend.toLocaleString()}</span>
                          {isAdmin && (
                            <Button size="sm" variant="ghost" className="h-6 text-[10px] px-2"
                              onClick={() => openIdentifiers('vendor', v.vendor_id, v.vendor_name)}>
                              <FileText className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="border rounded-xl p-12 text-center">
                  <DollarSign className="h-10 w-10 text-muted-foreground/20 mx-auto mb-3" />
                  <h3 className="text-sm font-semibold mb-1">No spend data yet</h3>
                  <p className="text-xs text-muted-foreground mb-4">Upload invoices or connect an ERP system to start tracking vendor spend.</p>
                  <div className="flex gap-2 justify-center">
                    <Button size="sm" variant="outline" className="text-xs gap-1.5" onClick={() => setShowInvoiceUpload(true)}>
                      <Upload className="h-3.5 w-3.5" /> Upload Invoice
                    </Button>
                    <Button size="sm" variant="outline" className="text-xs gap-1.5" onClick={() => setTab('connectors')}>
                      <Plug className="h-3.5 w-3.5" /> Connect ERP
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Invoices Tab */}
          {tab === 'invoices' && (
            <div className="space-y-4">
              {invoices.length > 0 ? (
                <div className="border rounded-xl overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/40">
                      <tr>
                        <th className="text-left p-2.5 font-semibold">Vendor</th>
                        <th className="p-2.5 font-semibold">Invoice #</th>
                        <th className="p-2.5 font-semibold">Date</th>
                        <th className="p-2.5 font-semibold text-right">Amount</th>
                        <th className="p-2.5 font-semibold">Status</th>
                        <th className="p-2.5 font-semibold">Source</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {invoices.map(inv => (
                        <tr key={inv.id} className="hover:bg-muted/20">
                          <td className="p-2.5 font-medium">{inv.vendor_name || 'Unknown'}</td>
                          <td className="p-2.5 text-center font-mono">{inv.invoice_number || '-'}</td>
                          <td className="p-2.5 text-center">{inv.invoice_date || '-'}</td>
                          <td className="p-2.5 text-right font-bold tabular-nums">${(inv.total_amount || 0).toLocaleString()}</td>
                          <td className="p-2.5 text-center">
                            <Badge variant="outline" className={`text-[9px] ${inv.status === 'paid' ? 'text-emerald-600 border-emerald-300' : inv.status === 'overdue' ? 'text-red-600 border-red-300' : 'text-amber-600 border-amber-300'}`}>{inv.status}</Badge>
                          </td>
                          <td className="p-2.5 text-center">
                            {inv.ai_extracted && <Badge className="text-[8px] bg-purple-100 text-purple-700">AI Parsed</Badge>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="border rounded-xl p-12 text-center">
                  <Receipt className="h-10 w-10 text-muted-foreground/20 mx-auto mb-3" />
                  <h3 className="text-sm font-semibold mb-1">No invoices tracked</h3>
                  <p className="text-xs text-muted-foreground mb-4">Upload an invoice or connect an accounting system to pull invoice data.</p>
                  <Button size="sm" className="gap-1.5 text-xs" onClick={() => setShowInvoiceUpload(true)}>
                    <Upload className="h-3.5 w-3.5" /> Upload First Invoice
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* AI Spend Analysis Tab */}
          {tab === 'analysis' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-bold">AI Spend Analysis</h3>
                  <p className="text-xs text-muted-foreground">GPT-4o analyzes your vendor spend for patterns, anomalies, and savings opportunities.</p>
                </div>
                <Button onClick={runSpendAnalysis} disabled={analyzingSpend} className="gap-1.5" data-testid="run-spend-analysis-btn">
                  {analyzingSpend ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Brain className="h-3.5 w-3.5" />}
                  {analyzingSpend ? 'Analyzing...' : 'Run AI Analysis'}
                </Button>
              </div>
              {spendAnalysis ? (
                <div className="space-y-4">
                  {spendAnalysis.executive_summary && (
                    <div className="p-4 rounded-xl border bg-muted/30 text-sm leading-relaxed">{spendAnalysis.executive_summary}</div>
                  )}
                  {(spendAnalysis.insights || []).length > 0 && (
                    <div>
                      <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Insights</h4>
                      <div className="space-y-2">
                        {spendAnalysis.insights.map((ins, i) => (
                          <div key={i} className={`p-3 rounded-lg border border-l-4 text-sm ${insightColors[ins.type] || insightColors.trend}`}>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-semibold">{ins.title}</span>
                              <Badge className="text-[8px]">{ins.type}</Badge>
                              <Badge variant="outline" className="text-[8px]">{ins.severity}</Badge>
                              {ins.estimated_impact && <span className="text-xs font-bold ml-auto">{ins.estimated_impact}</span>}
                            </div>
                            <p className="text-xs opacity-80">{ins.description}</p>
                            {ins.recommendation && <p className="text-xs mt-1 font-medium">{ins.recommendation}</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {(spendAnalysis.action_items || []).length > 0 && (
                    <div>
                      <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Action Items</h4>
                      <div className="space-y-1">
                        {spendAnalysis.action_items.map((item, i) => (
                          <div key={i} className="flex items-start gap-2 text-sm p-2 rounded bg-muted/30">
                            <span className="w-5 h-5 rounded-full bg-primary text-primary-foreground text-[10px] flex items-center justify-center shrink-0">{i + 1}</span>
                            <span>{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="border rounded-xl p-12 text-center">
                  <Brain className="h-10 w-10 text-muted-foreground/20 mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">Click &ldquo;Run AI Analysis&rdquo; to get AI-powered insights on your vendor spend.</p>
                </div>
              )}
            </div>
          )}

          {/* Alerts Tab (Anomaly Detector) */}
          {tab === 'alerts' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-bold">Spend Anomaly Detector</h3>
                  <p className="text-xs text-muted-foreground">Proactive alerts for duplicates, budget overruns, spend spikes, and untracked contracts.</p>
                </div>
                {anomalies?.summary && (
                  <div className="flex gap-2">
                    {anomalies.summary.high > 0 && <Badge className="bg-red-100 text-red-700 text-[10px]">{anomalies.summary.high} High</Badge>}
                    {anomalies.summary.medium > 0 && <Badge className="bg-amber-100 text-amber-700 text-[10px]">{anomalies.summary.medium} Medium</Badge>}
                    {anomalies.summary.low > 0 && <Badge className="bg-blue-100 text-blue-700 text-[10px]">{anomalies.summary.low} Low</Badge>}
                  </div>
                )}
              </div>
              {anomalies?.summary?.total_at_risk > 0 && (
                <div className="border rounded-xl p-4 bg-red-50/50 dark:bg-red-950/10">
                  <span className="text-xs text-muted-foreground">Total Amount at Risk</span>
                  <div className="text-2xl font-bold text-red-600" style={{ fontFamily: 'Space Grotesk' }}>${anomalies.summary.total_at_risk.toLocaleString()}</div>
                </div>
              )}
              {(anomalies?.alerts || []).length > 0 ? (
                <div className="space-y-2">
                  {anomalies.alerts.map((a, i) => {
                    const Icon = alertIcons[a.type] || AlertTriangle;
                    return (
                      <div key={i} className={`p-3 rounded-lg border border-l-4 ${alertColors[a.type] || 'bg-muted/30 border-l-muted-foreground'}`} data-testid={`alert-${i}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <Icon className="h-4 w-4 shrink-0" />
                          <span className="text-sm font-semibold flex-1">{a.title}</span>
                          <Badge variant="outline" className={`text-[8px] ${a.severity === 'high' ? 'text-red-600 border-red-300' : a.severity === 'medium' ? 'text-amber-600 border-amber-300' : 'text-blue-600 border-blue-300'}`}>{a.severity}</Badge>
                          {a.amount > 0 && <span className="text-sm font-bold tabular-nums">${a.amount.toLocaleString()}</span>}
                        </div>
                        <p className="text-xs text-muted-foreground">{a.description}</p>
                        {a.details && (
                          <div className="mt-1 flex gap-2 flex-wrap">
                            {a.details.map((d, j) => <Badge key={j} variant="outline" className="text-[8px]">{d.invoice_number || d.date}</Badge>)}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="border rounded-xl p-12 text-center">
                  <Check className="h-10 w-10 text-emerald-400 mx-auto mb-3" />
                  <h3 className="text-sm font-semibold mb-1">No anomalies detected</h3>
                  <p className="text-xs text-muted-foreground">Add invoices and spend data to enable anomaly detection.</p>
                </div>
              )}
            </div>
          )}

          {/* Spend vs Budget Tab */}
          {tab === 'budget' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-bold">Spend vs Budget</h3>
                  <p className="text-xs text-muted-foreground">Compare actual vendor spend against budgeted amounts.</p>
                </div>
              </div>
              {budgetData?.summary ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="border rounded-xl p-3 bg-card text-center"><div className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>${(budgetData.summary.total_budget || 0).toLocaleString()}</div><div className="text-[10px] text-muted-foreground">Total Budget</div></div>
                    <div className="border rounded-xl p-3 bg-card text-center"><div className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>${(budgetData.summary.total_actual || 0).toLocaleString()}</div><div className="text-[10px] text-muted-foreground">Actual Spend</div></div>
                    <div className="border rounded-xl p-3 bg-card text-center"><div className={`text-lg font-bold ${budgetData.summary.total_variance > 0 ? 'text-red-600' : 'text-emerald-600'}`} style={{ fontFamily: 'Space Grotesk' }}>${Math.abs(budgetData.summary.total_variance || 0).toLocaleString()}</div><div className="text-[10px] text-muted-foreground">{budgetData.summary.total_variance > 0 ? 'Over Budget' : 'Under Budget'}</div></div>
                    <div className="border rounded-xl p-3 bg-card text-center"><div className="text-lg font-bold text-amber-600" style={{ fontFamily: 'Space Grotesk' }}>{budgetData.summary.over_budget_count || 0}</div><div className="text-[10px] text-muted-foreground">Vendors Over Budget</div></div>
                  </div>
                  {(budgetData.comparisons || []).length > 0 && (
                    <div className="border rounded-xl overflow-hidden">
                      <table className="w-full text-xs">
                        <thead className="bg-muted/40"><tr><th className="text-left p-2.5 font-semibold">Vendor</th><th className="p-2.5 font-semibold">Period</th><th className="p-2.5 text-right font-semibold">Budget</th><th className="p-2.5 text-right font-semibold">Actual</th><th className="p-2.5 text-right font-semibold">Variance</th><th className="p-2.5 font-semibold">Status</th></tr></thead>
                        <tbody className="divide-y">
                          {budgetData.comparisons.map((c, i) => (
                            <tr key={i} className="hover:bg-muted/20">
                              <td className="p-2.5 font-medium">{c.vendor_name}</td>
                              <td className="p-2.5 text-center font-mono">{c.period}</td>
                              <td className="p-2.5 text-right tabular-nums">${c.budget.toLocaleString()}</td>
                              <td className="p-2.5 text-right tabular-nums font-medium">${c.actual.toLocaleString()}</td>
                              <td className={`p-2.5 text-right tabular-nums font-bold ${c.variance > 0 ? 'text-red-600' : 'text-emerald-600'}`}>{c.variance > 0 ? '+' : ''}${c.variance.toLocaleString()} ({c.variance_pct}%)</td>
                              <td className="p-2.5 text-center"><Badge variant="outline" className={`text-[8px] ${c.status === 'over' ? 'text-red-600 border-red-300' : c.status === 'under' ? 'text-blue-600 border-blue-300' : 'text-emerald-600 border-emerald-300'}`}>{c.status === 'over' ? 'Over' : c.status === 'under' ? 'Under' : 'On Track'}</Badge></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ) : (
                <div className="border rounded-xl p-12 text-center">
                  <Scale className="h-10 w-10 text-muted-foreground/20 mx-auto mb-3" />
                  <h3 className="text-sm font-semibold mb-1">No budget data yet</h3>
                  <p className="text-xs text-muted-foreground">Set vendor budgets and track actual spend against them. Upload invoices or connect an ERP to populate spend data.</p>
                </div>
              )}
            </div>
          )}

          {/* Financial Health Scorecards Tab */}
          {tab === 'health' && (
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-bold">Financial Health Scorecards</h3>
                <p className="text-xs text-muted-foreground">Per-property financial readiness: contract coverage, spend visibility, and financial ID completeness.</p>
              </div>
              {healthScores?.summary ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="border rounded-xl p-3 bg-card text-center">
                      <div className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk', color: healthScores.summary.avg_score >= 70 ? '#16a34a' : healthScores.summary.avg_score >= 40 ? '#d97706' : '#dc2626' }}>{healthScores.summary.avg_score}%</div>
                      <div className="text-[10px] text-muted-foreground">Avg Score</div>
                    </div>
                    <div className="border rounded-xl p-3 bg-card text-center"><div className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>{healthScores.summary.properties_analyzed}</div><div className="text-[10px] text-muted-foreground">Properties</div></div>
                    <div className="border rounded-xl p-3 bg-card text-center"><div className="text-2xl font-bold text-emerald-600" style={{ fontFamily: 'Space Grotesk' }}>{healthScores.summary.above_80}</div><div className="text-[10px] text-muted-foreground">Healthy (80%+)</div></div>
                    <div className="border rounded-xl p-3 bg-card text-center"><div className="text-2xl font-bold text-red-600" style={{ fontFamily: 'Space Grotesk' }}>{healthScores.summary.below_50}</div><div className="text-[10px] text-muted-foreground">At Risk (&lt;50%)</div></div>
                  </div>
                  {(healthScores.scorecards || []).length > 0 ? (
                    <div className="border rounded-xl overflow-hidden">
                      <table className="w-full text-xs" data-testid="health-scorecards-table">
                        <thead className="bg-muted/40">
                          <tr>
                            <th className="text-left p-2.5 font-semibold">Property</th>
                            <th className="p-2.5 font-semibold text-center">Overall</th>
                            <th className="p-2.5 font-semibold text-center">Contracts</th>
                            <th className="p-2.5 font-semibold text-center">Spend Vis.</th>
                            <th className="p-2.5 font-semibold text-center">Financial IDs</th>
                            <th className="p-2.5 font-semibold text-center">Products</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {healthScores.scorecards.map(s => (
                            <tr key={s.property_id} className="hover:bg-muted/20">
                              <td className="p-2.5 font-medium">{s.property_name}</td>
                              <td className="p-2.5 text-center">
                                <div className="flex items-center gap-1.5 justify-center">
                                  <div className="w-12 h-2 rounded-full bg-muted overflow-hidden">
                                    <div className="h-full rounded-full" style={{ width: `${s.overall_score}%`, backgroundColor: s.overall_score >= 70 ? '#16a34a' : s.overall_score >= 40 ? '#d97706' : '#dc2626' }} />
                                  </div>
                                  <span className="font-bold tabular-nums" style={{ color: s.overall_score >= 70 ? '#16a34a' : s.overall_score >= 40 ? '#d97706' : '#dc2626' }}>{s.overall_score}%</span>
                                </div>
                              </td>
                              <td className="p-2.5 text-center"><span className="tabular-nums">{s.with_contracts}/{s.deployed_products}</span></td>
                              <td className="p-2.5 text-center"><span className="tabular-nums">{s.vendors_tracked}/{s.total_vendors}</span></td>
                              <td className="p-2.5 text-center"><span className={`font-medium tabular-nums ${s.financial_id_coverage >= 50 ? 'text-emerald-600' : 'text-amber-600'}`}>{s.financial_id_coverage}%</span></td>
                              <td className="p-2.5 text-center">{s.deployed_products}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-8">No properties with deployed products found.</p>
                  )}
                </div>
              ) : (
                <div className="border rounded-xl p-12 text-center">
                  <BarChart3 className="h-10 w-10 text-muted-foreground/20 mx-auto mb-3" />
                  <h3 className="text-sm font-semibold mb-1">Financial Health Scorecards</h3>
                  <p className="text-xs text-muted-foreground">Add contracts, invoices, and financial identifiers to see property-level financial health scores.</p>
                </div>
              )}
            </div>
          )}

          {/* Benchmarks Tab */}
          {tab === 'benchmarks' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-bold">Cost Benchmarks</h3>
                  <p className="text-xs text-muted-foreground">Cost-per-room and cost-per-platform analysis across your portfolio.</p>
                </div>
                <div className="flex gap-1 bg-muted/60 rounded-lg p-0.5">
                  {[{id:'property',label:'Per Property'},{id:'platform',label:'Per Platform'},{id:'service',label:'By Service Level'}].map(v => (
                    <button key={v.id} onClick={() => setBenchView(v.id)}
                      className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${benchView === v.id ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
                      {v.label}
                    </button>
                  ))}
                </div>
              </div>

              {benchmarks?.summary && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="border rounded-xl p-3 bg-card text-center">
                    <div className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>${benchmarks.summary.portfolio_avg_cost_per_room.toLocaleString()}</div>
                    <div className="text-[10px] text-muted-foreground">Avg Cost/Room/Year</div>
                  </div>
                  <div className="border rounded-xl p-3 bg-card text-center">
                    <div className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>${benchmarks.summary.portfolio_total_cost.toLocaleString()}</div>
                    <div className="text-[10px] text-muted-foreground">Portfolio Total</div>
                  </div>
                  <div className="border rounded-xl p-3 bg-card text-center">
                    <div className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>{benchmarks.summary.properties_with_cost_data}</div>
                    <div className="text-[10px] text-muted-foreground">With Cost Data</div>
                  </div>
                  <div className="border rounded-xl p-3 bg-card text-center">
                    <div className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>{benchmarks.summary.properties_with_rooms}</div>
                    <div className="text-[10px] text-muted-foreground">With Room Counts</div>
                  </div>
                </div>
              )}

              {/* Per Property View */}
              {benchView === 'property' && (benchmarks?.properties || []).length > 0 && (
                <div className="border rounded-xl overflow-hidden">
                  <table className="w-full text-xs" data-testid="benchmark-property-table">
                    <thead className="bg-muted/40">
                      <tr>
                        <th className="text-left p-2.5 font-semibold">Property</th>
                        <th className="p-2.5 font-semibold text-center">Rooms</th>
                        <th className="p-2.5 font-semibold text-center">Service</th>
                        <th className="p-2.5 font-semibold text-center">Solutions</th>
                        <th className="p-2.5 font-semibold text-right">Total Cost</th>
                        <th className="p-2.5 font-semibold text-right">$/Room/Yr</th>
                        <th className="p-2.5 font-semibold text-right">$/Room/Mo</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {benchmarks.properties.map(p => (
                        <tr key={p.property_id} className="hover:bg-muted/20">
                          <td className="p-2.5 font-medium">{p.property_name}</td>
                          <td className="p-2.5 text-center tabular-nums">{p.rooms || '-'}</td>
                          <td className="p-2.5 text-center"><Badge variant="outline" className="text-[8px]">{p.service_level || '-'}</Badge></td>
                          <td className="p-2.5 text-center tabular-nums">{p.solutions}</td>
                          <td className="p-2.5 text-right tabular-nums font-medium">{p.total_annual_cost > 0 ? `$${p.total_annual_cost.toLocaleString()}` : '-'}</td>
                          <td className="p-2.5 text-right tabular-nums font-bold">{p.cost_per_room_annual > 0 ? `$${p.cost_per_room_annual.toLocaleString()}` : '-'}</td>
                          <td className="p-2.5 text-right tabular-nums">{p.cost_per_room_monthly > 0 ? `$${p.cost_per_room_monthly.toLocaleString()}` : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Per Platform View */}
              {benchView === 'platform' && (benchmarks?.platforms || []).length > 0 && (
                <div className="border rounded-xl overflow-hidden">
                  <table className="w-full text-xs" data-testid="benchmark-platform-table">
                    <thead className="bg-muted/40">
                      <tr>
                        <th className="text-left p-2.5 font-semibold">Platform</th>
                        <th className="p-2.5 font-semibold">Category</th>
                        <th className="p-2.5 font-semibold text-center">Deployments</th>
                        <th className="p-2.5 font-semibold text-right">Avg Cost</th>
                        <th className="p-2.5 font-semibold text-right">Median</th>
                        <th className="p-2.5 font-semibold text-right">Range</th>
                        <th className="p-2.5 font-semibold text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {benchmarks.platforms.filter(p => p.total_cost > 0 || p.deployments > 2).slice(0, 40).map(p => (
                        <tr key={p.platform_id} className="hover:bg-muted/20">
                          <td className="p-2.5 font-medium">{p.platform_name}</td>
                          <td className="p-2.5"><Badge variant="outline" className="text-[8px]">{p.category}</Badge></td>
                          <td className="p-2.5 text-center tabular-nums">{p.deployments}</td>
                          <td className="p-2.5 text-right tabular-nums font-medium">{p.avg_cost > 0 ? `$${p.avg_cost.toLocaleString()}` : '-'}</td>
                          <td className="p-2.5 text-right tabular-nums">{p.median_cost > 0 ? `$${p.median_cost.toLocaleString()}` : '-'}</td>
                          <td className="p-2.5 text-right tabular-nums text-muted-foreground">{p.min_cost > 0 ? `$${p.min_cost.toLocaleString()} - $${p.max_cost.toLocaleString()}` : '-'}</td>
                          <td className="p-2.5 text-right tabular-nums font-bold">{p.total_cost > 0 ? `$${p.total_cost.toLocaleString()}` : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Service Level View */}
              {benchView === 'service' && (benchmarks?.service_levels || []).length > 0 && (
                <div className="space-y-3">
                  {benchmarks.service_levels.map(sl => (
                    <div key={sl.service_level} className="border rounded-xl p-4 bg-card">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Badge className="text-xs capitalize">{sl.service_level}</Badge>
                          <span className="text-xs text-muted-foreground">{sl.properties} properties, {sl.total_rooms} rooms</span>
                        </div>
                        <span className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>
                          {sl.avg_cost_per_room > 0 ? `$${sl.avg_cost_per_room.toLocaleString()}/room/yr` : 'No cost data'}
                        </span>
                      </div>
                      {sl.total_cost > 0 && (
                        <div className="text-xs text-muted-foreground">Total technology spend: ${sl.total_cost.toLocaleString()}</div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Empty state */}
              {(!benchmarks?.properties?.length && !benchmarks?.platforms?.length) && (
                <div className="border rounded-xl p-12 text-center">
                  <Target className="h-10 w-10 text-muted-foreground/20 mx-auto mb-3" />
                  <h3 className="text-sm font-semibold mb-1">No benchmark data yet</h3>
                  <p className="text-xs text-muted-foreground">Add property room counts, contracts, and spend data to generate cost benchmarks.</p>
                </div>
              )}
            </div>
          )}

          {/* Reconciliation Tab */}
          {tab === 'reconciliation' && (
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-bold">Contract-to-Invoice Reconciliation</h3>
                <p className="text-xs text-muted-foreground">Match contracts against invoices to find billing discrepancies.</p>
              </div>
              {reconData?.summary ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                    <div className="border rounded-xl p-3 bg-card text-center"><div className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>{reconData.summary.total_contracts}</div><div className="text-[10px] text-muted-foreground">Contracts</div></div>
                    <div className="border rounded-xl p-3 bg-card text-center"><div className="text-lg font-bold text-emerald-600" style={{ fontFamily: 'Space Grotesk' }}>{reconData.summary.matched}</div><div className="text-[10px] text-muted-foreground">Matched</div></div>
                    <div className="border rounded-xl p-3 bg-card text-center"><div className="text-lg font-bold text-amber-600" style={{ fontFamily: 'Space Grotesk' }}>{reconData.summary.no_invoices}</div><div className="text-[10px] text-muted-foreground">No Invoices</div></div>
                    <div className="border rounded-xl p-3 bg-card text-center"><div className="text-lg font-bold text-red-600" style={{ fontFamily: 'Space Grotesk' }}>{reconData.summary.over_invoiced}</div><div className="text-[10px] text-muted-foreground">Over-Invoiced</div></div>
                    <div className="border rounded-xl p-3 bg-card text-center"><div className="text-lg font-bold text-blue-600" style={{ fontFamily: 'Space Grotesk' }}>{reconData.summary.under_invoiced}</div><div className="text-[10px] text-muted-foreground">Under-Invoiced</div></div>
                  </div>
                  {(reconData.reconciliations || []).length > 0 ? (
                    <div className="border rounded-xl overflow-hidden">
                      <table className="w-full text-xs">
                        <thead className="bg-muted/40"><tr><th className="text-left p-2.5 font-semibold">Vendor</th><th className="p-2.5 font-semibold">Product</th><th className="p-2.5 text-right font-semibold">Contract</th><th className="p-2.5 text-right font-semibold">Invoiced</th><th className="p-2.5 text-right font-semibold">Variance</th><th className="p-2.5 font-semibold">Status</th></tr></thead>
                        <tbody className="divide-y">
                          {reconData.reconciliations.slice(0, 30).map((r, i) => (
                            <tr key={i} className="hover:bg-muted/20">
                              <td className="p-2.5 font-medium">{r.vendor_name}</td>
                              <td className="p-2.5">{r.product_name || '-'}</td>
                              <td className="p-2.5 text-right tabular-nums">${r.contract_value.toLocaleString()}</td>
                              <td className="p-2.5 text-right tabular-nums">${r.invoiced_total.toLocaleString()}</td>
                              <td className={`p-2.5 text-right tabular-nums font-bold ${r.variance > 0 ? 'text-red-600' : r.variance < 0 ? 'text-emerald-600' : ''}`}>{r.variance !== 0 ? `$${r.variance.toLocaleString()}` : '-'}</td>
                              <td className="p-2.5 text-center"><Badge variant="outline" className={`text-[8px] ${r.status === 'over_invoiced' ? 'text-red-600 border-red-300' : r.status === 'no_invoices' ? 'text-amber-600 border-amber-300' : r.status === 'under_invoiced' ? 'text-blue-600 border-blue-300' : 'text-emerald-600 border-emerald-300'}`}>{r.status.replace('_', ' ')}</Badge></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-8">No contracts to reconcile. Add contracts in the Tech Database to enable reconciliation.</p>
                  )}
                </div>
              ) : (
                <div className="border rounded-xl p-12 text-center">
                  <BarChart3 className="h-10 w-10 text-muted-foreground/20 mx-auto mb-3" />
                  <h3 className="text-sm font-semibold mb-1">Contract Reconciliation</h3>
                  <p className="text-xs text-muted-foreground">Add contracts and invoices to see how billing matches up.</p>
                </div>
              )}
            </div>
          )}

          {/* Connectors Tab */}
          {tab === 'connectors' && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">Connect your financial systems to automatically sync vendor spend, invoices, and cost data.</p>
              {['accounting', 'erp', 'expense', 'procurement', 'ap', 'payment', 'manual'].map(cat => {
                const catConnectors = connectors.filter(c => c.category === cat);
                if (!catConnectors.length) return null;
                return (
                  <div key={cat}>
                    <h3 className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: catColors[cat] }}>
                      {cat === 'ap' ? 'Accounts Payable' : cat === 'erp' ? 'Enterprise Resource Planning' : cat.charAt(0).toUpperCase() + cat.slice(1)}
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {catConnectors.map(conn => (
                        <div key={conn.id} className={`border rounded-xl p-4 transition-colors ${conn.enabled ? 'border-emerald-300 bg-emerald-50/30 dark:bg-emerald-950/10' : 'hover:border-muted-foreground/30'}`}
                          data-testid={`connector-${conn.id}`}>
                          <div className="flex items-center gap-3 mb-2">
                            <div className="w-9 h-9 rounded-lg flex items-center justify-center text-[10px] font-bold border"
                              style={{ backgroundColor: `${catColors[cat]}10`, color: catColors[cat], borderColor: `${catColors[cat]}30` }}>
                              {conn.logo}
                            </div>
                            <div className="flex-1 min-w-0">
                              <span className="text-sm font-semibold block truncate">{conn.name}</span>
                              {conn.enabled && conn.last_sync && (
                                <span className="text-[10px] text-muted-foreground">Last sync: {new Date(conn.last_sync).toLocaleDateString()}</span>
                              )}
                            </div>
                            {conn.enabled && <Check className="h-4 w-4 text-emerald-600 shrink-0" />}
                          </div>
                          {isAdmin && (
                            <div className="flex gap-2 mt-2">
                              <Button size="sm" variant={conn.enabled ? 'outline' : 'default'} className="flex-1 text-xs h-7"
                                onClick={() => toggleConnector(conn.id, !conn.enabled)}>
                                {conn.enabled ? 'Disable' : 'Enable'}
                              </Button>
                              {conn.enabled && (
                                <Button size="sm" variant="outline" className="text-xs h-7 gap-1"
                                  onClick={() => syncConnector(conn.id)}>
                                  <RefreshCw className="h-3 w-3" /> Sync
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        </ScrollArea>

      {/* Invoice Upload Dialog */}
      <Dialog open={showInvoiceUpload} onOpenChange={setShowInvoiceUpload}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-purple-500" /> AI Invoice Parser
            </DialogTitle>
            <DialogDescription>Paste invoice text and AI will extract vendor, amounts, line items, and dates.</DialogDescription>
          </DialogHeader>
          {!parsedInvoice ? (
            <div className="space-y-3 py-2">
              <Textarea value={invoiceText} onChange={e => setInvoiceText(e.target.value)} rows={10}
                placeholder="Paste invoice text here... (copy from PDF, email, or accounting system)" data-testid="invoice-text-input" />
              <Button onClick={parseInvoice} disabled={parsing || !invoiceText.trim()} className="w-full gap-1.5" data-testid="parse-invoice-btn">
                {parsing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                {parsing ? 'AI Parsing...' : 'Parse Invoice with AI'}
              </Button>
            </div>
          ) : (
            <ScrollArea className="flex-1">
              <div className="space-y-3 py-2 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-[10px]">Vendor</Label><div className="font-medium">{parsedInvoice.vendor_name || '-'}</div>
                    {parsedInvoice.matched_vendor && <Badge className="text-[8px] bg-emerald-100 text-emerald-700 mt-1">Matched: {parsedInvoice.matched_vendor.name}</Badge>}
                  </div>
                  <div><Label className="text-[10px]">Invoice #</Label><div className="font-mono">{parsedInvoice.invoice_number || '-'}</div></div>
                  <div><Label className="text-[10px]">Date</Label><div>{parsedInvoice.invoice_date || '-'}</div></div>
                  <div><Label className="text-[10px]">Due Date</Label><div>{parsedInvoice.due_date || '-'}</div></div>
                  <div><Label className="text-[10px]">Total</Label><div className="text-lg font-bold text-emerald-700">${(parsedInvoice.total_amount || 0).toLocaleString()}</div></div>
                  <div><Label className="text-[10px]">Payment Terms</Label><div>{parsedInvoice.payment_terms || '-'}</div></div>
                </div>
                {(parsedInvoice.line_items || []).length > 0 && (
                  <div>
                    <Label className="text-[10px]">Line Items</Label>
                    <div className="border rounded-lg overflow-hidden mt-1">
                      <table className="w-full text-xs">
                        <thead className="bg-muted/40"><tr><th className="text-left p-2">Description</th><th className="p-2">Qty</th><th className="p-2 text-right">Amount</th><th className="p-2">Category</th></tr></thead>
                        <tbody className="divide-y">
                          {parsedInvoice.line_items.map((li, i) => (
                            <tr key={i}><td className="p-2">{li.description}</td><td className="p-2 text-center">{li.quantity || 1}</td><td className="p-2 text-right font-mono">${(li.amount || 0).toLocaleString()}</td><td className="p-2"><Badge variant="outline" className="text-[8px]">{li.platform_category || '-'}</Badge></td></tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                {parsedInvoice.notes && <div><Label className="text-[10px]">AI Notes</Label><p className="text-xs text-muted-foreground bg-muted/30 p-2 rounded">{parsedInvoice.notes}</p></div>}
              </div>
            </ScrollArea>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setShowInvoiceUpload(false); setParsedInvoice(null); setInvoiceText(''); }}>Cancel</Button>
            {parsedInvoice && (
              <>
                <Button variant="outline" onClick={() => setParsedInvoice(null)}>Re-parse</Button>
                <Button onClick={saveInvoice} disabled={savingInvoice} className="gap-1.5" data-testid="save-invoice-btn">
                  {savingInvoice ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                  Save Invoice
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Financial Identifiers Dialog */}
      <Dialog open={showIdentifiers} onOpenChange={setShowIdentifiers}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-sm">
              <FileText className="h-4 w-4" /> Financial Identifiers
            </DialogTitle>
            <DialogDescription className="text-xs">
              Financial codes for <strong>{identEntity.name}</strong> — used for ERP matching and spend tracking.
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="flex-1">
            <div className="space-y-3 py-2">
              {[
                { key: 'erp_vendor_id', label: 'ERP Vendor ID', placeholder: 'e.g., V-10042' },
                { key: 'accounting_vendor_code', label: 'Accounting Vendor Code', placeholder: 'e.g., VEND-ORACLE-001' },
                { key: 'tax_id', label: 'Tax ID / EIN', placeholder: 'e.g., 12-3456789' },
                { key: 'duns_number', label: 'DUNS Number', placeholder: 'e.g., 12-345-6789' },
                { key: 'cost_center_id', label: 'Cost Center ID', placeholder: 'e.g., CC-IT-500' },
                { key: 'gl_account', label: 'GL Account', placeholder: 'e.g., 6200-IT-Software' },
                { key: 'geo_code', label: 'Geo Code', placeholder: 'e.g., US-FL-ORL' },
                { key: 'spend_category', label: 'Spend / Revenue Category', placeholder: 'e.g., Technology - SaaS' },
                { key: 'expense_type', label: 'Expense Type', placeholder: 'e.g., Operating Expense' },
                { key: 'payment_terms', label: 'Payment Terms', placeholder: 'e.g., Net 30' },
                { key: 'currency', label: 'Currency', placeholder: 'USD' },
                { key: 'location_id', label: 'Location ID', placeholder: 'e.g., LOC-001' },
                { key: 'department_id', label: 'Department ID', placeholder: 'e.g., DEPT-IT' },
                { key: 'project_code', label: 'Project Code', placeholder: 'e.g., PRJ-2026-TECH' },
              ].map(f => (
                <div key={f.key}>
                  <Label className="text-xs">{f.label}</Label>
                  <Input value={identifiers[f.key] || ''} onChange={e => setIdentifiers(prev => ({ ...prev, [f.key]: e.target.value }))}
                    placeholder={f.placeholder} className="h-8 text-xs" data-testid={`ident-${f.key}`} />
                </div>
              ))}
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowIdentifiers(false)}>Cancel</Button>
            <Button onClick={saveIdentifiers} disabled={savingIdent} className="gap-1.5" data-testid="save-identifiers-btn">
              {savingIdent ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
              Save Identifiers
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SidebarLayout>
  );
}
